/**
 * sportRules.js
 *
 * Pure, framework-free helpers that encode official sport scoring rules so the
 * live scoreboards, Quick Score and unit tests all share one implementation.
 *
 *   - Rally-point games (badminton BWF, table tennis ITTF, volleyball FIVB)
 *   - Match format parsing from match_config (scoring_rules / game_rules)
 *   - Table tennis service rotation (ITTF 2.13)
 *   - Basketball period config (FIBA default) and the no-draw rule
 *   - Football extra time (2 x 15) and penalty shootout (IFAB Law 10)
 */
import { getSportConfigKey, SPORTS_CONFIG } from "../config/sportsConfig";

/** Value used throughout the app to mean "no points cap". */
export const NO_CAP = 999;

const firstDefined = (...values) => values.find((v) => v !== undefined && v !== null);

const toInt = (v) => {
  if (v === undefined || v === null || v === "") return undefined;
  const n = typeof v === "number" ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Read the match format chosen at creation.
 *
 * MatchCreation saves:
 *   match_config.scoring_rules = { points_to_win, win_by_margin, max_points }
 *   match_config.game_rules    = { total_games }
 * Schedule-generated / legacy matches may store the scoring values under
 * game_rules or at the top level, so those are used as fallbacks.
 *
 * @param {object} match
 * @param {object} defaults  { totalGames, ptsToWin, winMargin, maxPoints }
 */
export const readMatchFormat = (match, defaults = {}) => {
  const mc = match?.matchConfig || match?.match_config || {};
  const sr = mc.scoring_rules || {};
  const gr = mc.game_rules || {};

  const totalGames = firstDefined(
    toInt(gr.total_games), toInt(sr.total_games), toInt(mc.total_games),
    toInt(gr.total_sets), toInt(mc.total_sets), defaults.totalGames, 1
  );
  const ptsToWin = firstDefined(
    toInt(sr.points_to_win), toInt(gr.points_to_win), toInt(mc.points_to_win),
    toInt(gr.points_to_win_set), defaults.ptsToWin
  );
  const winMargin = firstDefined(
    toInt(sr.win_by_margin), toInt(gr.win_by_margin), toInt(mc.win_by_margin), defaults.winMargin, 2
  );
  const maxPoints = firstDefined(
    toInt(sr.max_points), toInt(gr.max_points), toInt(mc.max_points), defaults.maxPoints, NO_CAP
  );

  return { totalGames, ptsToWin, winMargin, maxPoints, gamesToWin: Math.ceil(totalGames / 2) };
};

/**
 * Winner of a rally-point game, or null while the game is still running.
 *
 *   1. Cap: whoever reaches maxPoints first wins (BWF 30-29). NO_CAP / null = no cap.
 *   2. Normal: score >= ptsToWin and lead >= winMargin. A margin of 0 or 1
 *      both mean "first to ptsToWin" (a lead of at least one point).
 *
 * @returns "p1" | "p2" | null
 */
export const checkRallyGameWinner = (p1, p2, ptsToWin, winMargin = 2, maxPoints = NO_CAP) => {
  const mx = Math.max(p1, p2);
  if (mx === 0 || p1 === p2) return null;

  if (maxPoints && maxPoints < NO_CAP && mx >= maxPoints) {
    if (p1 > p2) return "p1";
    if (p2 > p1) return "p2";
    return null;
  }

  if (!ptsToWin || mx < ptsToWin) return null;
  const margin = Math.max(1, winMargin || 0);
  if (Math.abs(p1 - p2) < margin) return null;
  return p1 > p2 ? "p1" : "p2";
};

// ─── Badminton (BWF) ────────────────────────────────────────────────────────

export const BADMINTON_DEFAULTS = { totalGames: 3, ptsToWin: 21, winMargin: 2, maxPoints: 30 };

export const getBadmintonFormat = (match) => readMatchFormat(match, BADMINTON_DEFAULTS);

/** BWF 7.x: the winner of a game serves first in the next game. */
export const getNextGameFirstServer = (previousGameWinner) => previousGameWinner;

// ─── Table tennis (ITTF) ────────────────────────────────────────────────────

export const TABLE_TENNIS_DEFAULTS = { totalGames: 5, ptsToWin: 11, winMargin: 2, maxPoints: NO_CAP };

export const getTableTennisFormat = (match) => {
  const fmt = readMatchFormat(match, TABLE_TENNIS_DEFAULTS);
  // Table tennis has no points cap. Old matches saved with the former
  // default cap of 15 (or 30 from the generic fallback) are treated as no cap.
  return { ...fmt, maxPoints: NO_CAP, winMargin: Math.max(2, fmt.winMargin || 0) };
};

export const isTableTennisDoubles = (match) => {
  const mc = match?.matchConfig || match?.match_config || {};
  const gr = mc.game_rules || {};
  return gr.doubles === true || gr.format === "doubles" || mc.match_type === "doubles";
};

/**
 * Serves each player makes in a row before service changes (LS-B17):
 * 2 in 11-point games (ITTF 2.13.3), 5 in the legacy 21-point format.
 */
export const getTableTennisServesPerTurn = (ptsToWin = 11) => (ptsToWin >= 21 ? 5 : 2);

/**
 * Who serves the next point in a table tennis game (ITTF 2.13.3).
 * Service alternates every 2 points (every 5 in 21-point games); from deuce
 * (both players at ptsToWin - 1, i.e. 10-10 or 20-20) it alternates every point.
 *
 * @param {number} p1 current points of player/pair 1
 * @param {number} p2 current points of player/pair 2
 * @param {"p1"|"p2"} firstServer who served first in this game
 * @param {number} ptsToWin 11 by default
 * @returns "p1" | "p2"
 */
export const getTableTennisServer = (p1, p2, firstServer = "p1", ptsToWin = 11) => {
  const total = p1 + p2;
  const deuceAt = ptsToWin - 1;
  const perTurn = getTableTennisServesPerTurn(ptsToWin);
  const other = firstServer === "p1" ? "p2" : "p1";
  let turn;
  if (p1 >= deuceAt && p2 >= deuceAt) {
    // deuceAt * 2 points were played to reach deuce (e.g. 10 turns of 2 to
    // reach 10-10, 8 turns of 5 to reach 20-20) — from there service
    // alternates every point.
    const base = deuceAt * 2;
    turn = Math.floor(base / perTurn) + (total - base);
  } else {
    turn = Math.floor(total / perTurn);
  }
  return turn % 2 === 0 ? firstServer : other;
};

/** Serves remaining for the current server in this turn (1–2, or 1–5 in 21-point games). */
export const getTableTennisServesLeft = (p1, p2, ptsToWin = 11) => {
  const deuceAt = ptsToWin - 1;
  if (p1 >= deuceAt && p2 >= deuceAt) return 1;
  const perTurn = getTableTennisServesPerTurn(ptsToWin);
  return perTurn - ((p1 + p2) % perTurn);
};

/** ITTF 2.13.2: the player who served first in a game receives first in the next. */
export const getTableTennisGameFirstServer = (gameNumber, matchFirstServer = "p1") => {
  const other = matchFirstServer === "p1" ? "p2" : "p1";
  return (gameNumber - 1) % 2 === 0 ? matchFirstServer : other;
};

// ─── Volleyball (FIVB) ──────────────────────────────────────────────────────

export const VOLLEYBALL_DEFAULTS = { totalGames: 5, ptsToWin: 25, winMargin: 2, maxPoints: NO_CAP };
export const VOLLEYBALL_DECIDING_SET_POINTS = 15;

/**
 * Volleyball format. The deciding set (the last possible set of a multi-set
 * match: set 5 of 5, set 3 of 3) is played to 15. Volleyball has no cap.
 */
export const getVolleyballFormat = (match) => {
  const fmt = readMatchFormat(match, VOLLEYBALL_DEFAULTS);
  const totalSets = fmt.totalGames;
  const finalSetPts = totalSets > 1 ? VOLLEYBALL_DECIDING_SET_POINTS : fmt.ptsToWin;
  return {
    totalSets,
    ptsToWinSet: fmt.ptsToWin,
    winMargin: fmt.winMargin,
    setsToWin: fmt.gamesToWin,
    finalSetPts,
    maxPoints: NO_CAP,
  };
};

// ─── Sports whose dedicated board manages games/sets itself ─────────────────

const SELF_MANAGED_SPORTS = ["volleyball", "badminton", "tabletennis"];

/**
 * True when the sport's dedicated scoreboard detects game/set/match wins on
 * its own, so the generic single-score win/cap popups must not run.
 */
export const sportManagesOwnGames = (sportName) =>
  SELF_MANAGED_SPORTS.includes(getSportConfigKey(sportName));

// ─── Draws ──────────────────────────────────────────────────────────────────

const NO_DRAW_SPORTS = ["basketball", "volleyball", "badminton", "tabletennis", "tennis"];

/** Whether a match in this sport may end in a draw. */
export const sportAllowsDraw = (sportName) => !NO_DRAW_SPORTS.includes(getSportConfigKey(sportName));

// ─── Substitutions during a live match ──────────────────────────────────────
// Mirrored on the backend in services/match/matchLineupRules.js — keep in sync.
//   pairedReturn: a sub may only go back out for the player they replaced (FIVB)
//   maxPerSet:    substitutions per team per set (null = unlimited)
//   maxPerMatch:  substitutions per team per match (null = unlimited)
//   noReentry:    a player who was substituted off may not come back on
//
// Football follows IFAB Law 3 by default (5 substitutes, no re-entry). A match
// can relax it for grassroots play with match_config.substitution_rules
// { max_per_match: number | null, allow_reentry: boolean } (AS-B07).
// The volleyball libero is not modelled.

export const SUBSTITUTION_RULES = {
  volleyball: { enabled: true, pairedReturn: true, maxPerSet: 6, maxPerMatch: null, noReentry: false },
  basketball: { enabled: true, pairedReturn: false, maxPerSet: null, maxPerMatch: null, noReentry: false },
  football: { enabled: true, pairedReturn: false, maxPerSet: null, maxPerMatch: 5, noReentry: true },
};

const NO_SUBSTITUTIONS = { enabled: false, pairedReturn: false, maxPerSet: null, maxPerMatch: null, noReentry: false };

const readSubstitutionOverrides = (matchConfig) => {
  const mc = matchConfig?.matchConfig || matchConfig?.match_config || matchConfig || {};
  const raw = mc.substitution_rules;
  return raw && typeof raw === "object" ? raw : {};
};

/**
 * Substitution rules for a sport, with the match's own overrides applied.
 * Badminton, tennis and table tennis (singles and doubles) have no substitutions.
 *
 * @param {string} sportName
 * @param {Object} [matchConfig]  match_config (or the match itself)
 */
export const getSubstitutionRules = (sportName, matchConfig = null) => {
  const base = SUBSTITUTION_RULES[getSportConfigKey(sportName)];
  if (!base) return NO_SUBSTITUTIONS;
  if (getSportConfigKey(sportName) !== "football") return base;
  const overrides = readSubstitutionOverrides(matchConfig);
  const rules = { ...base };
  if (overrides.max_per_match === null) rules.maxPerMatch = null;
  else if (Number.isInteger(overrides.max_per_match) && overrides.max_per_match >= 0) {
    rules.maxPerMatch = overrides.max_per_match;
  }
  if (typeof overrides.allow_reentry === "boolean") rules.noReentry = !overrides.allow_reentry;
  return rules;
};

// ─── Basketball (FIBA default) ──────────────────────────────────────────────

export const getBasketballFormat = (match) => {
  const mc = match?.matchConfig || match?.match_config || {};
  const sr = mc.scoring_rules || {};
  const gr = mc.game_rules || {};
  const d = SPORTS_CONFIG.basketball.defaults;
  const periods = firstDefined(toInt(sr.periods), toInt(gr.periods), toInt(mc.periods), d.periods);
  const periodMinutes = firstDefined(
    toInt(sr.period_minutes), toInt(gr.period_minutes), toInt(mc.period_minutes), d.period_minutes
  );
  const overtimeMinutes = firstDefined(
    toInt(sr.overtime_minutes), toInt(gr.overtime_minutes), toInt(mc.overtime_minutes), d.overtime_minutes
  );
  return {
    periods,
    periodMinutes,
    periodSeconds: periodMinutes * 60,
    overtimeMinutes,
    overtimeSeconds: overtimeMinutes * 60,
  };
};

/** "Q1".."Q4" for regulation, then "OT1", "OT2", … for every overtime. */
export const getBasketballPeriodLabel = (period, periods = 4) =>
  period <= periods ? `Q${period}` : `OT${period - periods}`;

/**
 * What the board may offer when a period ends.
 * Basketball has no draws: a tie after regulation (or after an overtime)
 * always goes to another overtime, and the game cannot be ended as a draw.
 */
export const getBasketballPeriodEndAction = (period, periods, p1, p2) => {
  if (period < periods) return "next_period";
  if (p1 === p2) return "overtime";
  return "end_game";
};

// ─── Football (IFAB) ────────────────────────────────────────────────────────

/** Phases: 1 = 1st half, 2 = 2nd half, 3 = ET 1st half, 4 = ET 2nd half, 5 = penalties. */
export const FOOTBALL_PHASE = { FIRST_HALF: 1, SECOND_HALF: 2, ET_FIRST: 3, ET_SECOND: 4, PENALTIES: 5 };

export const getFootballFormat = (match) => {
  const mc = match?.matchConfig || match?.match_config || {};
  const sr = mc.scoring_rules || {};
  const halfMinutes = firstDefined(toInt(sr.half_minutes), toInt(mc.half_minutes), 45);
  const extraTimeMinutes = firstDefined(toInt(sr.extra_time_minutes), toInt(mc.extra_time_minutes), 15);
  return {
    halfMinutes,
    halfSeconds: halfMinutes * 60,
    extraTimeMinutes,
    extraTimeSeconds: extraTimeMinutes * 60,
  };
};

export const getFootballPhaseLabel = (phase) => {
  switch (phase) {
    case 1: return "1st Half";
    case 2: return "2nd Half";
    case 3: return "Extra Time 1st Half";
    case 4: return "Extra Time 2nd Half";
    case 5: return "Penalty Shootout";
    default: return "Full Time";
  }
};

/**
 * What the board may offer when a phase ends with the given score.
 *   1st half          → start 2nd half
 *   2nd half, level   → extra time (or end as a draw where allowed)
 *   ET 1st half       → start ET 2nd half
 *   ET 2nd half, level→ penalty shootout
 *   otherwise         → end game
 */
export const getFootballPhaseEndAction = (phase, p1, p2) => {
  if (phase === 1) return "second_half";
  if (phase === 3) return "et_second_half";
  if (phase === 2 && p1 === p2) return "extra_time";
  if (phase === 4 && p1 === p2) return "penalties";
  return "end_game";
};

/**
 * Penalty shootout state (IFAB Law 10).
 * kicks: array of { team: "p1"|"p2", scored: boolean } in the order taken.
 * Teams alternate; first `rounds` (5) kicks each, ending early when one team
 * cannot catch up; then sudden death in pairs.
 */
export const getPenaltyShootoutState = (kicks = [], firstKicker = "p1", rounds = 5) => {
  const second = firstKicker === "p1" ? "p2" : "p1";
  const taken = { p1: 0, p2: 0 };
  const scored = { p1: 0, p2: 0 };
  let winner = null;

  for (const k of kicks) {
    if (winner) break;
    taken[k.team] += 1;
    if (k.scored) scored[k.team] += 1;

    if (taken.p1 <= rounds && taken.p2 <= rounds && (taken.p1 < rounds || taken.p2 < rounds)) {
      // Regulation kicks: stop as soon as one side cannot catch up.
      const leftP1 = rounds - taken.p1;
      const leftP2 = rounds - taken.p2;
      if (scored.p1 > scored.p2 + leftP2) winner = "p1";
      else if (scored.p2 > scored.p1 + leftP1) winner = "p2";
    } else if (taken.p1 === taken.p2 && scored.p1 !== scored.p2) {
      // End of regulation round 5, or end of a sudden-death pair.
      winner = scored.p1 > scored.p2 ? "p1" : "p2";
    }
  }

  const nextKicker = winner
    ? null
    : (taken[firstKicker] <= taken[second] ? firstKicker : second);
  const suddenDeath = Math.min(taken.p1, taken.p2) >= rounds;

  return { taken, scored, winner, nextKicker, suddenDeath, rounds };
};
